"use client"

import HomeDetailsWrapperAccordion from "../home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import { workScopeStatusOptions } from "../../constants/options"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const BASE_FIELD_NAME = "home.cabinetInstallInfo"


function HomeCabinetInstallForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillCabinetInstallOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillCabinetInstallOn` })
    const goodwillCounterInstallOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillCounterInstallOn` })
    const goodwillApplianceInstallOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillApplianceInstallOn` })
    
    // Watch status fields and claimed by fields
    const cabinetInstall = useWatch({ control, name: `${BASE_FIELD_NAME}.cabinetInstall` })
    const cabinetClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.cabinetClaimedBy` })
    const counterInstall = useWatch({ control, name: `${BASE_FIELD_NAME}.counterInstall` })
    const counterClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.counterClaimedBy` })
    const applianceInstall = useWatch({ control, name: `${BASE_FIELD_NAME}.applianceInstall` })
    const applianceClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.applianceClaimedBy` })
    const goodwillCabinetInstall = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillCabinetInstall` })
    const goodwillCabinetInstallClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillCabinetInstallClaimedBy`,
    })
    const goodwillCounterInstall = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillCounterInstall` })
    const goodwillCounterInstallClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillCounterInstallClaimedBy`,
    })
    const goodwillApplianceInstall = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillApplianceInstall` })
    const goodwillApplianceInstallClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillApplianceInstallClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: cabinetInstall,
        claimedBy: cabinetClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.cabinetClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: counterInstall,
        claimedBy: counterClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.counterClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: applianceInstall,
        claimedBy: applianceClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.applianceClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillCabinetInstall,
        claimedBy: goodwillCabinetInstallClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillCabinetInstallClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillCabinetInstallOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillCounterInstall,
        claimedBy: goodwillCounterInstallClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillCounterInstallClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillCounterInstallOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillApplianceInstall,
        claimedBy: goodwillApplianceInstallClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillApplianceInstallClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillApplianceInstallOn,
    })

    return (
        <HomeDetailsWrapperAccordion 
            heading="Cabinet Install"
            wrapperClassName="xl:grid-cols-2"
        >
            <FormSelectInput
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Cabinet Install" toggleName={`${BASE_FIELD_NAME}.goodwillCabinetInstallOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.cabinetInstall`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Cabinet Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.cabinetClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillCabinetInstallOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Cabinet Install Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillCabinetInstall`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Cabinet Install Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillCabinetInstallClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Row 2: Linear feet questions */}
            <FormTextInput
                inputGroupProps={{
                    label: "Linear feet of kitchen cabinet lowers needed",
                }}
                placeholder="Linear feet of kitchen cabinet lowers needed"
                name={`${BASE_FIELD_NAME}.kitchenCabinetLowers`}
                type="number"
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "Linear feet of kitchen cabinet uppers needed",
                }}
                placeholder="Linear feet of kitchen cabinet uppers needed"
                name={`${BASE_FIELD_NAME}.kitchenCabinetUppers`}
                type="number"
                control={control}
            />
            <FormSelectInput
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Counter Install" toggleName={`${BASE_FIELD_NAME}.goodwillCounterInstallOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.counterInstall`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Counter Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.counterClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillCounterInstallOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Counter Install Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillCounterInstall`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Counter Install Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillCounterInstallClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <FormSelectInput
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Appliance Install" toggleName={`${BASE_FIELD_NAME}.goodwillApplianceInstallOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.applianceInstall`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Appliance Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.applianceClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillApplianceInstallOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Appliance Install Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillApplianceInstall`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Appliance Install Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillApplianceInstallClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Add any other detail you can about the kitchen cabinet install needs",
                    wrapperClassName: "xl:col-span-2"
                }}
                rows={4}
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeCabinetInstallForm
